import { useEffect, useRef, useState } from "react";
import { Loader2, Crown, Brain, Target } from "lucide-react";
import { toast } from "sonner";
import { getWeeklyInsights, submitInsightsSatisfaction } from "../../lib/weekly";

// Stage 2D — saved Weekly Insights for one submission + required satisfaction rating.
const POLL_MS = 4000;

export function Section({ icon, title, items, testid }) {
  return (
    <section className="surface-card p-6 md:p-8" data-testid={testid}>
      <h3 className="font-serif text-2xl md:text-3xl text-brand-ink flex items-center gap-3">
        <span className="text-brand-primary">{icon}</span>{title}
      </h3>
      <ul className="mt-5 space-y-4">
        {items.map((t, i) => (
          <li key={i} className="flex gap-3 text-brand-ink text-base leading-relaxed" data-testid={`${testid}-item-${i + 1}`}>
            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-brand-primary shrink-0" aria-hidden />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

export const RED_SUPPORT_MESSAGE = "You've had a really rough week, and I really appreciate your honesty and courage to share it here. Please know you're not alone in this. Rest assured that Justine will be made aware of your situation on Monday and will reach out to you as soon as she possibly can to support you 💗";
const SUPPORT_QUESTION = "Is there anything specific you need Justine's eyes or hands on right now? This might be something you've already mentioned in your check-in, but we don't want to guess what you'd specifically like her help with. If there's anything you'd like Justine to look at, review, adjust or follow up with you about, please share it here and she'll reach out :)";

export function SatisfactionForm({ submissionId, onRated, current = false }) {
  const [rating, setRating] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [support, setSupport] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!rating || busy) return;
    setBusy(true);
    try {
      const payload = { rating, feedback: feedback.trim() || null };
      if (current) payload.support_request = support;
      await submitInsightsSatisfaction(submissionId, payload);
      onRated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your rating. Please try again.");
      if (e?.response?.status === 409) onRated();
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="surface-card p-6 md:p-8" data-testid="weekly-satisfaction">
      <h3 className="font-serif text-2xl text-brand-ink" data-testid="weekly-satisfaction-question">How satisfied are you by this week&apos;s check in?</h3>
      <div className="mt-3 text-sm text-brand-ink-muted space-y-1">
        <p data-testid="weekly-satisfaction-low">1 = this sucked</p>
        <p data-testid="weekly-satisfaction-high">10 = this was just what I needed</p>
      </div>
      <div className="mt-5 flex flex-wrap gap-2" role="radiogroup" aria-label="Satisfaction rating" data-testid="weekly-rating-options">
        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
          <button key={n} type="button" role="radio" aria-checked={rating === n} onClick={() => setRating(n)}
            className={`h-11 w-11 rounded-full border text-sm font-bold transition-colors ${rating === n ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border text-brand-ink hover:border-brand-ink"}`}
            data-testid={`weekly-rating-${n}`} data-selected={rating === n ? "true" : "false"}>
            {n}
          </button>
        ))}
      </div>
      <label className="label-eyebrow block mt-8 mb-1" htmlFor="satisfaction-feedback" data-testid="weekly-feedback-label">Help us to understand your rating above</label>
      <p className="text-xs text-brand-ink-muted mb-2" data-testid="weekly-feedback-optional">(Optional)</p>
      <textarea id="satisfaction-feedback" className="input-warm text-base min-h-[90px]" value={feedback}
        onChange={(e) => setFeedback(e.target.value)} data-testid="weekly-feedback-input" />
      {current && (
        <div className="mt-8 rounded-xl border border-brand-border bg-brand-surface/60 p-5" data-testid="weekly-support-request">
          <label className="label-eyebrow block mb-1" htmlFor="support-request" data-testid="weekly-support-request-label">Need Justine&apos;s help with something specific?</label>
          <p className="text-xs text-brand-ink-muted mb-2" data-testid="weekly-support-request-optional">(Optional)</p>
          <p className="text-sm md:text-base text-brand-ink leading-relaxed mb-3" data-testid="weekly-support-request-question">{SUPPORT_QUESTION}</p>
          <textarea id="support-request" className="input-warm text-base min-h-[90px]" value={support}
            onChange={(e) => setSupport(e.target.value)} data-testid="weekly-support-request-input" />
        </div>
      )}
      <button type="button" onClick={submit} disabled={!rating || busy}
        className="btn-primary inline-flex items-center gap-2 mt-6 disabled:opacity-50 disabled:cursor-not-allowed" data-testid="weekly-finish-btn">
        {busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : "Finish my check in"}
      </button>
    </section>
  );
}

export function InsightSections({ rec, testidPrefix = "weekly-insights" }) {
  return (
    <>
      <Section icon={<Crown size={22} strokeWidth={1.6} />} title="Your Wins This Week 👑" items={rec.wins} testid={`${testidPrefix}-wins`} />
      <Section icon={<Brain size={22} strokeWidth={1.6} />} title="Insights From Your Week 🧠" items={rec.insights} testid={`${testidPrefix}-insights`} />
      <Section icon={<Target size={22} strokeWidth={1.6} />} title="Your Next Steps Over The Next 7 Days To Support You 🎯" items={rec.next_steps} testid={`${testidPrefix}-next-steps`} />
    </>
  );
}

export function formatWeekOf(identifier) {
  if (!identifier) return "";
  const [y, m, d] = identifier.split("-").map(Number);
  return `Week of ${new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })}`;
}

export default function WeeklyInsights({ submissionId, preferredName, onRated }) {
  const [rec, setRec] = useState(null);
  const timer = useRef(null);
  const name = preferredName || "there";

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const r = await getWeeklyInsights(submissionId);
        if (cancelled) return;
        setRec(r);
        if (r.status !== "completed") timer.current = setTimeout(load, POLL_MS);
      } catch (_e) {
        if (!cancelled) timer.current = setTimeout(load, POLL_MS);
      }
    };
    load();
    return () => { cancelled = true; clearTimeout(timer.current); };
  }, [submissionId]);

  if (!rec || rec.status !== "completed") {
    return (
      <section className="surface-card mt-6 p-8 flex items-start gap-4" data-testid="weekly-insights-processing">
        <Loader2 className="text-brand-primary animate-spin shrink-0 mt-1" size={22} />
        <div>
          <p className="font-serif text-2xl text-brand-ink">Your check in has been safely received.</p>
          <p className="mt-2 text-brand-ink-muted text-base leading-relaxed">Your personalised Weekly Insights are being prepared now.</p>
        </div>
      </section>
    );
  }

  return (
    <div className="mt-6 space-y-6" data-testid="weekly-insights">
      <Section icon={<Crown size={22} strokeWidth={1.6} />} title="Your Wins This Week 👑" items={rec.wins} testid="weekly-insights-wins" />
      <Section icon={<Brain size={22} strokeWidth={1.6} />} title="Insights From Your Week 🧠" items={rec.insights} testid="weekly-insights-insights" />
      {rec.is_red && (
        <section className="rounded-2xl border-2 border-[#C8965A] bg-[#FBF3E8] p-6 md:p-8" role="status" data-testid="weekly-red-support-message">
          <p className="font-serif text-xl md:text-2xl text-brand-ink leading-snug">{RED_SUPPORT_MESSAGE}</p>
        </section>
      )}
      <Section icon={<Target size={22} strokeWidth={1.6} />} title="Your Next Steps Over The Next 7 Days To Support You 🎯" items={rec.next_steps} testid="weekly-insights-next-steps" />
      {rec.satisfaction_submitted ? (
        <section className="surface-card p-6 md:p-8 border-l-4 border-l-brand-primary" data-testid="weekly-final-message">
          <p className="font-serif text-2xl md:text-3xl text-brand-ink leading-snug">
            Great job on completing your check in {name}! If there is anything this week that you need more support with please share that in our community or send a message to Justine
          </p>
        </section>
      ) : (
        <SatisfactionForm submissionId={submissionId} current onRated={() => { setRec((r) => ({ ...r, satisfaction_submitted: true })); if (onRated) onRated(); }} />
      )}
    </div>
  );
}
